import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { ApiClientError } from '@/lib/http-core';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { useArtistFiltersStore, type ArtistSort } from '@/stores/useArtistFiltersStore';
import type { ArtistAdminListItem } from '@/lib/api-admin/types';
import { ArtistFiltersBar } from '../components/ArtistFiltersBar';
import { ArtistTable } from '../components/ArtistTable';
import { ArtistArchiveModal } from '../components/ArtistArchiveModal';
import { ArtistDeleteModal } from '../components/ArtistDeleteModal';
import { useArtistsQuery } from '../hooks/useArtistsQuery';
import { useArchiveArtistMutation, usePurgeArtistMutation } from '../hooks/useArtistMutation';
import { useUpdateArtistMutation } from '../hooks/useArtistMutation';
import { toast } from '@/components/ui';

type ArtistUpdateBody = Parameters<ReturnType<typeof useUpdateArtistMutation>['mutate']>[0]['body'];

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

const ARTIST_SORT_VALUES = new Set<ArtistSort>([
  'stageName,asc',
  'stageName,desc',
  'createdAt,desc',
  'createdAt,asc',
  'updatedAt,desc',
  'productCount,desc',
]);

const isArtistSort = (s: string): s is ArtistSort =>
  ARTIST_SORT_VALUES.has(s as ArtistSort);

export const ArtistListPage = () => {
  const { t } = useTranslation('artists');

  const keyword = useArtistFiltersStore((s) => s.keyword);
  const type = useArtistFiltersStore((s) => s.type);
  const status = useArtistFiltersStore((s) => s.status);
  const vendorId = useArtistFiltersStore((s) => s.vendorId);
  const hasNoImage = useArtistFiltersStore((s) => s.hasNoImage);
  const page = useArtistFiltersStore((s) => s.page);
  const size = useArtistFiltersStore((s) => s.size);
  const sort = useArtistFiltersStore((s) => s.sort);
  const setSort = useArtistFiltersStore((s) => s.setSort);
  const setPage = useArtistFiltersStore((s) => s.setPage);

  const query = useArtistsQuery({
    page,
    size,
    sort,
    keyword,
    type,
    status,
    vendorId,
    hasNoImage,
  });

  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const items = query.data?.items ?? [];

  // 246: 목록에서 직접 삭제(소프트 삭제 = 비활성). 상세 페이지와 동일한 확인 모달을 재사용한다.
  const archive = useArchiveArtistMutation();
  const [deleteTarget, setDeleteTarget] = useState<ArtistAdminListItem | null>(null);
  // QA-593 노출/숨김 전환 — PATCH {displayed}.
  const displayUpdate = useUpdateArtistMutation();
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const deleteError = errorMessageOf(archive.error);

  const closeDelete = () => {
    setDeleteTarget(null);
    archive.reset();
  };

  const purge = usePurgeArtistMutation();
  const [purgeTarget, setPurgeTarget] = useState<ArtistAdminListItem | null>(null);
  const purgeError = errorMessageOf(purge.error);

  const closePurge = () => {
    setPurgeTarget(null);
    purge.reset();
  };

  return (
    <PageShell
      title={t('artists.list.title')}
      actions={
        <Button asChild variant="cta">
          <Link to="/artists/new" aria-label={t('artists.actions.create')}>
            {t('artists.actions.create')}
          </Link>
        </Button>
      }
    >
      <ArtistFiltersBar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('artists.list.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('artists.actions.retry')}
          </Button>
        </div>
      ) : (
        <ArtistTable
          items={items}
          sort={sort}
          onSortChange={(next) => {
            if (isArtistSort(next)) setSort(next);
          }}
          onDelete={(artistId) => {
            const target = items.find((a) => a.artistId === artistId) ?? null;
            archive.reset();
            setDeleteTarget(target);
          }}
          deletingId={archive.isPending ? deleteTarget?.artistId ?? null : null}
          onPurge={(artistId) => {
            const target = items.find((a) => a.artistId === artistId) ?? null;
            purge.reset();
            setPurgeTarget(target);
          }}
          purgingId={purge.isPending ? purgeTarget?.artistId ?? null : null}
          onToggleDisplayed={(artistId, next) => {
            setTogglingId(artistId);
            displayUpdate.mutate(
              { artistId, body: { displayed: next } as unknown as ArtistUpdateBody },
              {
                onSuccess: () => toast.success(next ? '쇼핑몰에 노출했습니다.' : '쇼핑몰에서 숨겼습니다.'),
                onError: (e) => toast.error(e.message || '노출 상태를 바꾸지 못했습니다.'),
                onSettled: () => setTogglingId(null),
              },
            );
          }}
          togglingDisplayedId={togglingId}
        />
      )}

      <Pagination
        total={totalItems}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        aria-label={t('artists.list.paginationLabel')}
      />

      <ArtistArchiveModal
        isOpen={deleteTarget !== null}
        artistName={deleteTarget?.stageName ?? ''}
        productCount={deleteTarget?.productCount ?? 0}
        isSubmitting={archive.isPending}
        errorMessage={deleteError}
        onCancel={closeDelete}
        onConfirm={() => {
          if (!deleteTarget) return;
          archive.mutate(deleteTarget.artistId, {
            onSuccess: () => closeDelete(),
          });
        }}
      />

      <ArtistDeleteModal
        isOpen={purgeTarget !== null}
        artistName={purgeTarget?.stageName ?? ''}
        productCount={purgeTarget?.productCount ?? 0}
        isSubmitting={purge.isPending}
        errorMessage={purgeError}
        onCancel={closePurge}
        onConfirm={() => {
          if (!purgeTarget) return;
          purge.mutate(purgeTarget.artistId, {
            onSuccess: () => closePurge(),
          });
        }}
      />
    </PageShell>
  );
};
